import { useState, useEffect, useCallback } from 'react';
import { useAuth0Token } from './useAuth0Token';
import {
  getAllAdvisorConfigs,
  getAdvisorConfig,
  updateAdvisorConfig,
  uploadAdvisorPhoto,
  uploadCompanyLogo,
  getCachedAdvisorConfigs,
  getCachedAdvisorConfig,
  invalidateAdvisorConfigCache,
  AdvisorConfig
} from '../services/advisorConfigService';

/**
 * Hook to fetch all advisor configs (filtered by user permissions)
 */
export const useAdvisorConfigs = (enableCache: boolean = true) => {
  const { token } = useAuth0Token();
  const [configs, setConfigs] = useState<AdvisorConfig[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchConfigs = useCallback(async (forceRefresh: boolean = false) => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const data = enableCache
        ? await getCachedAdvisorConfigs(token, forceRefresh)
        : await getAllAdvisorConfigs(token);

      setConfigs(data);
    } catch (err: any) {
      console.error('Error fetching advisor configs:', err);
      setError(err.response?.data?.message || err.message || 'Failed to fetch advisor configs');
    } finally {
      setLoading(false);
    }
  }, [token, enableCache]);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  const refresh = useCallback(() => {
    return fetchConfigs(true);
  }, [fetchConfigs]);

  return {
    configs,
    loading,
    error,
    refresh
  };
};

/**
 * Hook to fetch single advisor config by customer ID
 */
export const useAdvisorConfig = (customerId: string | null, enableCache: boolean = true) => {
  const { token } = useAuth0Token();
  const [config, setConfig] = useState<AdvisorConfig | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchConfig = useCallback(async () => {
    if (!token || !customerId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const data = enableCache
        ? await getCachedAdvisorConfig(token, customerId)
        : await getAdvisorConfig(token, customerId);

      setConfig(data);
    } catch (err: any) {
      console.error(`Error fetching advisor config for ${customerId}:`, err);
      setError(err.response?.data?.message || err.message || 'Failed to fetch advisor config');
    } finally {
      setLoading(false);
    }
  }, [token, customerId, enableCache]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const refresh = useCallback(() => {
    return fetchConfig();
  }, [fetchConfig]);

  return {
    config,
    loading,
    error,
    refresh
  };
};

/**
 * Hook for updating advisor config with optimistic updates
 */
export const useUpdateAdvisorConfig = () => {
  const { token } = useAuth0Token();
  const [updating, setUpdating] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const update = useCallback(async (
    customerId: string,
    updates: Partial<AdvisorConfig>
  ): Promise<AdvisorConfig | null> => {
    if (!token) {
      setError('Not authenticated');
      return null;
    }

    try {
      setUpdating(true);
      setError(null);

      const updated = await updateAdvisorConfig(token, customerId, updates);

      // Invalidate cache so next fetch gets fresh data
      invalidateAdvisorConfigCache();

      return updated;
    } catch (err: any) {
      console.error(`Error updating advisor config for ${customerId}:`, err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to update advisor config';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setUpdating(false);
    }
  }, [token]);

  return {
    update,
    updating,
    error
  };
};

/**
 * Hook for uploading advisor photo
 */
export const useUploadAdvisorPhoto = () => {
  const { token } = useAuth0Token();
  const [uploading, setUploading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<number>(0);

  const upload = useCallback(async (
    customerId: string,
    file: File
  ): Promise<string | null> => {
    if (!token) {
      setError('Not authenticated');
      return null;
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Invalid file type. Please upload JPG, PNG, or WEBP image.');
      return null;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError('File size too large. Maximum size is 5MB.');
      return null;
    }

    try {
      setUploading(true);
      setError(null);
      setProgress(0);

      // Simulate progress (since we don't have real upload progress)
      const progressInterval = setInterval(() => {
        setProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      const result = await uploadAdvisorPhoto(token, customerId, file);

      clearInterval(progressInterval);
      setProgress(100);

      // Invalidate cache
      invalidateAdvisorConfigCache();

      return result.advisorPhotoUrl;
    } catch (err: any) {
      console.error('Error uploading advisor photo:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to upload photo';
      setError(errorMessage);
      return null;
    } finally {
      setUploading(false);
      setTimeout(() => setProgress(0), 1000);
    }
  }, [token]);

  return {
    upload,
    uploading,
    error,
    progress
  };
};

/**
 * Hook for uploading company logo
 */
export const useUploadCompanyLogo = () => {
  const { token } = useAuth0Token();
  const [uploading, setUploading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<number>(0);

  const upload = useCallback(async (
    customerId: string,
    file: File
  ): Promise<string | null> => {
    if (!token) {
      setError('Not authenticated');
      return null;
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Invalid file type. Please upload JPG, PNG, or WEBP image.');
      return null;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError('File size too large. Maximum size is 5MB.');
      return null;
    }

    try {
      setUploading(true);
      setError(null);
      setProgress(0);

      // Simulate progress
      const progressInterval = setInterval(() => {
        setProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      const result = await uploadCompanyLogo(token, customerId, file);

      clearInterval(progressInterval);
      setProgress(100);

      // Invalidate cache
      invalidateAdvisorConfigCache();

      return result.companyLogoUrl;
    } catch (err: any) {
      console.error('Error uploading company logo:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to upload logo';
      setError(errorMessage);
      return null;
    } finally {
      setUploading(false);
      setTimeout(() => setProgress(0), 1000);
    }
  }, [token]);

  return {
    upload,
    uploading,
    error,
    progress
  };
};
